import React from "react"

// styling
import styled from "@emotion/styled"

// components
import { Typography, Card, Badge, Button, Row } from "antd"
import { PlusOutlined, StarFilled } from "@ant-design/icons"

// store
import { useMenu } from "store/menu"
import { useResto } from "store/resto"
import { useTranslation } from "react-i18next"

// utils
import { assembleMenuItems } from "utils/menu"
import { matchTimeRanges } from "utils"

const HorizontalSwipeScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  background-color: #f0f0f0; /* Optional: background color */
  margin: 0 -24px; /* Optional: margin for spacing */
  padding: 16px 24px; /* Optional: padding for spacing */

  // Hide scrollbar only for small screens

  @media (max-width: 768px) {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer and Edge */
    &::-webkit-scrollbar {
      display: none; /* Safari and Chrome */
    }
  }

  & > * {
    flex: 0 0 auto; /* Prevent items from shrinking */
    margin-right: 16px; /* Space between items */
  }
  & > :last-of-type {
    margin-right: 0; /* Remove margin from the last item */
  }
  & > :first-of-type {
    margin-left: 0; /* Remove margin from the first item */
  }
  & > *:not(:last-of-type) {
    padding-right: 16px; /* Optional: add padding to the right of each item */
  }
  & > *:not(:first-of-type) {
    padding-left: 16px; /* Optional: add padding to the left of each item */
  }
  & > * {
    box-sizing: border-box; /* Ensure padding and borders are included in the element's total width */
    min-width: 200px; /* Set a minimum width for each item */
    max-width: 300px; /* Set a maximum width for each item */
    flex-shrink: 0; /* Prevent items from shrinking */
    flex-grow: 0; /* Prevent items from growing */
    flex-basis: auto; /* Allow items to take their natural width */
  }
`

const MenuFame = ({ onSelect }) => {
  const { menu } = useMenu()
  const restoSettings = useResto()
  const { t } = useTranslation()

  if (!restoSettings) return null

  const items = restoSettings.popular
    ? restoSettings.popular.items
        .map((item) => {
          const category = menu.find((cat) => cat.key === item.category_id)

          if (!category) {
            console.warn(
              `Menu item with category_id ${item.category_id} not found in menu.`
            )
            return null
          }

          const itemDetails = category.items.find((menuItem) => {
            const menuId = !isNaN(Number(menuItem.id))
              ? Number(menuItem.id)
              : menuItem.id
            const itemId = !isNaN(Number(item.item_id))
              ? Number(item.item_id)
              : item.item_id

            return menuId === itemId
          })
          if (!itemDetails) {
            console.warn(
              `Item with item_id ${item.item_id} not found in category ${category.key}.`
            )
            return null
          }

          const noOrderDay =
            !category.times ||
            Object.values(category.times).every((item) => item === null)
              ? false
              : !category.times[new Date().getDay()]
          const outOfOrder =
            !category.times ||
            Object.values(category.times).every((item) => item === null)
              ? false
              : category.times && !matchTimeRanges(category.times)

          const orderable = !noOrderDay && !outOfOrder

          if (!orderable) {
            console.warn(
              `Item with item_id ${item.item_id} is not orderable due to time restrictions.`
            )
            return null
          }

          return (
            <Badge.Ribbon
              key={item.item_id}
              text={<StarFilled />}
              size="small"
              color="red"
            >
              <Card
                key={item.item_id}
                style={{
                  minWidth: 200,
                }}
                size="small"
                hoverable
                onClick={() => {
                  const foodItem = assembleMenuItems(itemDetails, category)

                  onSelect(foodItem)
                }}
              >
                <Typography.Title
                  level={5}
                  italic
                  style={{ margin: 0 }}
                  type="secondary"
                >
                  {category.label}
                </Typography.Title>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {itemDetails.name}
                </Typography.Title>
                <Typography.Title
                  level={5}
                  style={{ margin: 0, marginBottom: 10 }}
                >
                  {Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency: "EUR",
                  }).format(itemDetails.preis)}
                </Typography.Title>
                <Typography.Paragraph italic type="secondary">
                  {itemDetails.info}
                </Typography.Paragraph>
                <Row justify="end">
                  <Button icon={<PlusOutlined />} size="small" type="dashed">
                    {t("add")}
                  </Button>
                </Row>

                {/* <Typography.Paragraph>
                      {Math.round(item.appearance_percentage)}% der Bestellungen
                      enthalten dieses Gericht.
                    </Typography.Paragraph> */}
              </Card>
            </Badge.Ribbon>
          )
        })
        .filter((item) => item !== null)
    : []

  return (
    <>
      {restoSettings &&
        menu &&
        restoSettings.popular &&
        restoSettings.popular.items.length > 0 &&
        items.length > 0 && (
          <div>
            <Typography.Title
              level={4}
              style={{
                marginTop: "1rem",
                fontStyle: "italic",
              }}
            >
              {t("popular_dishes")}
            </Typography.Title>

            <HorizontalSwipeScrollContainer data-testid="menu-fame">
              {items.map((item) => item)}
            </HorizontalSwipeScrollContainer>
          </div>
        )}
    </>
  )
}

export default React.memo(MenuFame, () => {
  return true
})
